import React, {
  useState, useEffect, useRef, useCallback,
} from 'react';
import { useAuth } from '../context/AuthContext';
import { useLiveRefresh } from '../context/LiveUpdatesContext';
import { useTheme } from '../context/ThemeContext';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Home,
  Building2,
  Users,
  Settings,
  FileText,
  UserCog,
  List,
  CheckSquare,
  Calendar,
  Clock,
  Heart,
  Utensils,
  ArrowLeft,
  ArrowRight,
  ChevronDown,
  GraduationCap,
  Activity,
  Megaphone,
  LifeBuoy,
  ClipboardCheck,
  Coins,
  Package,
  Award,
  Mail,
  ClipboardList,
  Flame,
  MessageSquare,
  UserPlus,
  CalendarOff,
  ShieldCheck,
  NotebookPen,
  Inbox,
  Briefcase,
  ClipboardList as RequestsIcon,
  CalendarCheck,
  Timer,
  Building,
  CalendarClock,
} from 'lucide-react';

import Logo from './Logo';
import { chatService } from '../services';
// One shared table of menu code -> route/icon/label/priority, so the sidebar
// and the post-login landing page can never disagree about where a module is.
import { menuCodeMap, hasDashboardMenu, isAgencyAdminRole } from '../config/menuCodeMap';
import { agencyService } from '../services/agencyService';
import openShiftsService from '../services/carehome_manager/openShiftsService';

const Sidebar = ({ isOpen, setIsOpen, isCollapsed, setIsCollapsed }) => {
  const { user, allAvailableMenus, menuIds, isMenuActive } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  // Domiciliary orgs care for "clients" in their own homes, not "residents".
  const isDomiciliary =
    (user?.organization?.organization_type || '').toUpperCase() === 'DOMICILIARY';

  /**
   * Has this user been granted one of these menu codes?
   *
   * The same authority the main menu list uses (`isMenuActive` on the menu's id),
   * so a nav item can never appear for somebody an admin hasn't assigned it to.
   * Several menus are stored under more than one spelling, hence a list.
   */
  const hasMenu = (codes) =>
    (allAvailableMenus || []).some((m) => codes.includes(m.code) && isMenuActive(m.id));

  const hasStaffMenu = hasMenu(['staffs', 'staff']);
  // Leave & Approval is assignable, so it is shown only where it was granted —
  // moving it inside the Staff group must not turn it into something everyone
  // with the Staff menu can see.
  const canSeeLeaveApproval = hasMenu(['leave_approval', 'leave_approvals']);
  // Payroll is now an assignable menu as well: managers always have it by role,
  // and anybody else sees it once an admin grants them the Payroll menu. The API
  // accepts both (isManager() covers the 'staffs' role), so neither case 403s.
  const isManagerRole = ['carehome_owner', 'domiciliary_owner', 'carehome_manager', 'domiciliary_manager']
    .includes(user?.role?.role_code);
  const canSeePayroll = isManagerRole || hasMenu(['payroll']);
  // Agencies — care homes only. Care-home managers and owners hold it by role
  // (the API admits them the same way); anybody else once it is granted.
  const canSeeAgencies = !isDomiciliary
    && (['carehome_owner', 'carehome_manager'].includes(user?.role?.role_code) || hasMenu(['agencies']));
  // A staffing agency's administrator gets the agency workspace instead of
  // every care-home module (see the agency nav below).
  const isAgency = isAgencyAdminRole(user);
  // Audits & policies is assignable like any other module, but managers hold
  // it by role too — a manager whose menu_ids predate the menu must never be
  // locked out of their own audits. When they HAVE been granted it the dynamic
  // list below draws it, so the fixed fallback is only for the ungranted case
  // and the two can't produce a duplicate row.
  const hasAuditsMenu = hasMenu(['audits_policies']);
  const showAuditsFallback = isManagerRole && !hasAuditsMenu;

  // Does `pathname` sit at or under `base`? A plain startsWith() is wrong here:
  // '/manager/staffs-report' starts with '/manager/staffs' but is a different
  // module, so it used to light up the Staff menu. Only an exact match or a
  // real path segment ('/base/...') counts.
  const isUnder = (pathname, base) =>
    pathname === base || pathname.startsWith(`${base}/`);

  // The Staff menu is an expandable group, shown for both care-home and
  // domiciliary managers. Auto-open while on any staff route so the active child
  // is visible.
  //
  // Two of the children are conditional, and both conditions matter:
  //   • Leave & Approval is an ASSIGNABLE menu — it appears only where an admin
  //     granted it. Hardcoding it here would hand it to everyone with the Staff
  //     menu, and the nav would disagree with what the API allows.
  //   • Payroll is manager-only, enforced in the page and again in the API.
  const staffChildren = [
    { label: 'Staff Management', href: '/manager/staffs', icon: Users },
    // Shortened for the nav — the child pills only have ~150px of label room in
    // the 256px sidebar, and the full "Recruitment & Onboarding" ellipsised.
    // The page itself still carries the full title.
    { label: 'Recruitment', href: '/manager/recruitment', icon: UserPlus },
    { label: 'Staff Compliance', href: '/manager/staff-compliance', icon: ClipboardCheck },
    ...(canSeeLeaveApproval
      ? [{ label: 'Leave & Approval', href: '/manager/leave-approvals', icon: CalendarOff }]
      : []),
    ...(canSeePayroll
      ? [{ label: 'Payroll', href: '/manager/payroll', icon: Coins }]
      : []),
    ...(canSeeAgencies
      ? [{ label: 'Agencies', href: '/manager/agencies', icon: Briefcase }]
      : []),
  ];
  // Only the group's own routes — deliberately not a '/manager/staff' prefix,
  // which would also swallow Staffs Report.
  const isStaffRoute = (pathname) =>
    isUnder(pathname, '/manager/staffs')
    || isUnder(pathname, '/manager/recruitment')
    || isUnder(pathname, '/manager/staff-compliance')
    || isUnder(pathname, '/manager/payroll')
    || isUnder(pathname, '/manager/leave-approvals')
    || isUnder(pathname, '/manager/agencies');
  const [staffOpen, setStaffOpen] = useState(false);
  useEffect(() => {
    if (isStaffRoute(location.pathname)) setStaffOpen(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  // Rota is an expandable group ONLY for domiciliary orgs (Schedule + Care
  // Packages). Care-home managers keep Rota as a plain link to the duty roster.
  const rotaChildren = [
    { label: 'Schedule', href: '/manager/domiciliary-schedule', icon: Calendar },
    { label: 'Care Packages', href: '/manager/care-packages', icon: Package },
  ];
  const isRotaRoute = (pathname) =>
    isUnder(pathname, '/manager/domiciliary-schedule') || isUnder(pathname, '/manager/care-packages');
  const [rotaOpen, setRotaOpen] = useState(false);
  useEffect(() => {
    if (isRotaRoute(location.pathname)) setRotaOpen(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  // Care homes: Rota is a group too — Schedule (the duty roster) + Available
  // Shifts (extra shifts offered to free carers), with a badge for carers'
  // requests waiting for a decision. Not for an agency carer using the home's
  // dashboard: the API refuses them Available Shifts (AGENCY_DASHBOARD_AREAS).
  const isAgencyWorker = Boolean(user?.agency_worker);
  const showCareRotaGroup = !isDomiciliary && !isAgency && !isAgencyWorker && hasMenu(['duty_roster']);
  const [openShiftRequests, setOpenShiftRequests] = useState(0);
  const loadOpenShiftRequests = useCallback(() => {
    openShiftsService.summary()
      .then((res) => setOpenShiftRequests(res?.data?.pending_requests || 0))
      .catch(() => { /* a failed badge poll is not worth surfacing */ });
  }, []);
  useEffect(() => {
    if (!showCareRotaGroup) return undefined;
    loadOpenShiftRequests();
    // Live refresh (below) moves it the moment a carer asks; this is a safety net.
    const timer = setInterval(loadOpenShiftRequests, 5 * 60 * 1000);
    return () => clearInterval(timer);
  }, [showCareRotaGroup, location.pathname, loadOpenShiftRequests]);
  useLiveRefresh(['rota'], () => { if (showCareRotaGroup) loadOpenShiftRequests(); });
  const careRotaChildren = [
    { label: 'Schedule', href: '/manager/duty-roster', icon: Calendar },
    { label: 'Available Shifts', href: '/manager/available-shifts', icon: CalendarClock, badge: openShiftRequests },
  ];
  const isCareRotaRoute = (pathname) =>
    isUnder(pathname, '/manager/duty-roster') || isUnder(pathname, '/manager/available-shifts');
  const [careRotaOpen, setCareRotaOpen] = useState(false);
  useEffect(() => {
    if (isCareRotaRoute(location.pathname)) setCareRotaOpen(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  // Care Records → expandable group (Care Notes + Handovers), one grant: the
  // care_notes menu. Both are the same feed; Handovers is scoped to notes the
  // carer shared to handover, as in the mobile app's side menu.
  const careRecordChildren = [
    { label: 'Care Notes', href: '/manager/care-notes', icon: NotebookPen },
    { label: 'Handovers', href: '/manager/handovers', icon: Inbox },
  ];
  const isCareRecordRoute = (pathname) =>
    isUnder(pathname, '/manager/care-notes') || isUnder(pathname, '/manager/handovers');
  const [careRecordsOpen, setCareRecordsOpen] = useState(false);
  useEffect(() => {
    if (isCareRecordRoute(location.pathname)) setCareRecordsOpen(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  // Residents / Clients → an expandable group once Enquiries & Assessments is
  // granted. Without that menu it stays a plain link to the residents list, so
  // nobody gets a group with one child in it.
  const hasEnquiriesMenu = hasMenu(['enquiries']);
  const residentsChildren = [
    { label: isDomiciliary ? 'Clients' : 'Residents', href: '/manager/residences', icon: Users },
    { label: 'Enquiries & Assessments', href: '/manager/enquiries-assessments', icon: ClipboardList },
  ];
  const isResidentsRoute = (pathname) =>
    isUnder(pathname, '/manager/residences')
    || isUnder(pathname, '/manager/enquiries-assessments')
    || isUnder(pathname, '/manager/enquiries');
  const [residentsOpen, setResidentsOpen] = useState(false);
  useEffect(() => {
    if (isResidentsRoute(location.pathname)) setResidentsOpen(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  // Collapsed-rail flyout: hovering a group icon (Staff / Rota) pops out a
  // floating card with the group's children, since there's no room for an
  // inline submenu on the narrow rail. Positioned with `fixed` so it escapes
  // the nav's overflow clipping.
  const [flyout, setFlyout] = useState(null); // { key, label, children, top }
  const flyoutTimer = useRef(null);
  const openFlyout = (data) => {
    if (flyoutTimer.current) clearTimeout(flyoutTimer.current);
    setFlyout(data);
  };
  const closeFlyoutSoon = () => {
    flyoutTimer.current = setTimeout(() => setFlyout(null), 140);
  };

  // Messages is a fixed item rather than an assignable menu: private chat is
  // for everyone who works in the organisation, so a new manager or staff
  // member can be reached the moment their account exists — no admin step.
  // Platform admins are excluded; they sit above organisations and have no one
  // to message.
  // Carer roles are here too: a carer whose manager also granted them dashboard
  // modules is signed in to the dashboard like anybody else, and the API's
  // isManager() lets them through on their menus. Platform admins excluded.
  const chatRoles = [
    'carehome_owner', 'domiciliary_owner',
    'carehome_manager', 'domiciliary_manager', 'staffs',
    'carehome_employee', 'domiciliary_employee',
  ];
  const canMessage = chatRoles.includes(user?.role?.role_code);
  const [unreadChats, setUnreadChats] = useState(0);

  useEffect(() => {
    if (!canMessage) return undefined;
    const load = () => {
      chatService
        .getUnreadCount()
        .then((res) => setUnreadChats(res?.data?.unread || 0))
        .catch(() => { /* a failed badge poll is not worth surfacing */ });
    };
    load();
    // Matches the NotificationBell cadence — cheap COUNT, once a minute.
    const timer = setInterval(load, 60 * 1000);
    return () => clearInterval(timer);
  }, [canMessage]);

  // Agency workspace badges: care homes waiting for a decision, and cover
  // requests nobody has answered yet. Same once-a-minute cadence.
  const [agencyCounts, setAgencyCounts] = useState({ pending_homes: 0, needs_response: 0 });
  const loadAgencyCounts = useCallback(() => {
    agencyService.overview()
      .then((res) => setAgencyCounts(res?.data || {}))
      .catch(() => { /* a failed badge poll is not worth surfacing */ });
  }, []);
  useEffect(() => {
    if (!isAgency) return undefined;
    loadAgencyCounts();
    // The live refresh below moves them the moment a home changes anything;
    // this slow poll is only a safety net.
    const timer = setInterval(loadAgencyCounts, 5 * 60 * 1000);
    return () => clearInterval(timer);
  }, [isAgency, location.pathname, loadAgencyCounts]);
  useLiveRefresh(['agency'], () => { if (isAgency) loadAgencyCounts(); });

  const agencyNav = [
    { label: 'Overview', href: '/agency/overview', icon: Home },
    { label: 'Requests', href: '/agency/requests', icon: RequestsIcon, badge: agencyCounts.needs_response },
    { label: 'Bookings', href: '/agency/bookings', icon: CalendarCheck },
    { label: 'Rota', href: '/agency/rota', icon: Calendar },
    { label: 'Staff', href: '/agency/staff', icon: Users },
    { label: 'Hours', href: '/agency/hours', icon: Timer },
    { label: 'Care homes', href: '/agency/care-homes', icon: Building, badge: agencyCounts.pending_homes, amber: true },
  ];

  // Reaching the Messages page clears the badge without waiting for the poll.
  useEffect(() => {
    if (location.pathname === '/manager/messages') setUnreadChats(0);
  }, [location.pathname]);

  // Does this user get the dashboard overview at all? Managers always do;
  // anyone else needs the Dashboard module ticked for them. Somebody without
  // it has no Dashboard button and lands on their first module instead.
  const canSeeDashboard = hasDashboardMenu(user, allAvailableMenus, isMenuActive);

  const getDashboardRoute = () => {
    // Carehome / domiciliary managers and anyone granted the Dashboard module
    // land on the manager dashboard — the same route RootRedirect sends them to
    // right after login, so clicking the sidebar's Dashboard button doesn't
    // bounce them to the admin dashboard.
    if (canSeeDashboard) return '/manager/dashboard';
    if (user?.role?.role_code === 'staffs') return '/manager/dashboard';
    return '/dashboard';
  };


  const visibleMenuItems = allAvailableMenus
    .map((menu) => {
      const menuInfo = menuCodeMap[menu.code];

      if (!menuInfo) {
        console.warn(`⚠️ No menuInfo found for code: "${menu.code}" - Please add it to menuCodeMap`);
        return null;
      }

      const isActive = isMenuActive(menu.id);

      return {
        id: menu.id,
        code: menu.code,
        label: (menu.code === 'residents' && isDomiciliary) ? 'Clients' : menuInfo.label,
        // Domiciliary managers don't use the care-home duty roster — their "Rota"
        // opens the domiciliary schedule directly.
        href: (menu.code === 'duty_roster' && isDomiciliary) ? '/manager/domiciliary-schedule' : menuInfo.route,
        icon: menuInfo.icon,
        priority: menuInfo.priority,
        careHomeOnly: menuInfo.careHomeOnly === true,
        hiddenFromSidebar: menuInfo.hiddenFromSidebar === true,
        isActive: isActive,
      };
    })
    .filter(Boolean)
    .filter((item) => item.isActive)
    // Care-home-only modules (e.g. Evacuation Plan) are hidden for domiciliary
    // orgs even when the menu is assigned to the user.
    .filter((item) => !(item.careHomeOnly && isDomiciliary))
    // Modules whose route still works but which are deliberately not drawn
    // here: To-Do (it lives in the top navbar's HeaderTodo popover), E-Learning
    // and Domestics (hidden per request), and the Dashboard module (drawn by
    // the fixed Dashboard button above the list, not as a menu item). Flagged
    // in one place now — see config/menuCodeMap.js.
    .filter((item) => !item.hiddenFromSidebar)
    // Leave & Approval now lives INSIDE the Staff group (see staffChildren), so
    // it is dropped from the top level — but only when that group is actually
    // there to hold it. Somebody granted Leave & Approval WITHOUT the Staff menu
    // has no group to find it in, so for them it stays a top-level item rather
    // than disappearing from the nav altogether.
    .filter((item) => !(
      ['leave_approval', 'leave_approvals'].includes(item.code) && hasStaffMenu
    ))
    // Same rule for the other Staff sub-menus: inside the group when the Staff
    // menu is granted, a top-level item of their own when it isn't.
    .filter((item) => !(
      ['recruitment', 'staff_compliance', 'payroll', 'agencies'].includes(item.code) && hasStaffMenu
    ))
    // Enquiries & Assessments is drawn INSIDE the Residents group, so it is
    // dropped from the top level — but only when that group exists. Somebody
    // granted Enquiries WITHOUT the Residents menu keeps it as a top-level item
    // rather than losing it from the nav altogether.
    .filter((item) => !(
      item.code === 'enquiries' && hasMenu(['residents'])
    ))
    // Maintenance reports is a slice of Staffs Report — somebody holding both
    // sees the one full page, not two links to it.
    .filter((item) => !(
      item.code === 'staff_reports_maintenance' && hasMenu(['staffs_reports', 'staffs_report'])
    ));

  const sortedMenuItems = visibleMenuItems.sort((a, b) => a.priority - b.priority);

  const isRouteActive = (route) => location.pathname === route;

  // Renders an expandable menu group (parent pill + tree of children). Shared by
  // the Staff and (domiciliary) Rota menus so both look and behave identically.
  const renderMenuGroup = ({ item, label, open, setOpen, children, collapsedHref, isGroupActive }) => {
    const GIcon = item.icon;
    // A child's badge (e.g. Available Shifts requests) shows on the parent
    // while the group is closed or the rail is collapsed.
    const groupBadge = children.reduce((t, c) => t + (c.badge > 0 ? c.badge : 0), 0);
    const badgePill = (n, extra = '') => (
      <span className={`bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center min-w-[20px] h-5 px-1.5 ${extra}`}>
        {n > 99 ? '99+' : n}
      </span>
    );

    // Collapsed rail: no room for a submenu — clicking opens the primary child.
    if (isCollapsed) {
      return (
        <li
          key={item.id}
          className="relative"
          onMouseEnter={(e) => {
            const rect = e.currentTarget.getBoundingClientRect();
            openFlyout({ key: item.id, label, children, top: rect.top });
          }}
          onMouseLeave={closeFlyoutSoon}
        >
          <button
            onClick={() => { navigate(collapsedHref); setIsOpen(false); }}
            title={label}
            className={`relative flex items-center transition-all duration-200 font-medium text-[15px] mx-auto justify-center w-[46px] h-[46px] rounded-xl ${isGroupActive
              ? 'bg-blue-600 text-white'
              : 'text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-[#31373f]'}`}
          >
            <GIcon className={`w-[22px] h-[22px] flex-shrink-0 ${isGroupActive ? 'text-white' : 'text-gray-400 dark:text-gray-500'}`} strokeWidth={1.8} />
            {groupBadge > 0 && (
              <span className="absolute top-1 right-1 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center min-w-[16px] h-4 px-1">
                {groupBadge > 99 ? '99+' : groupBadge}
              </span>
            )}
          </button>
        </li>
      );
    }

    return (
      <li key={item.id}>
        {/* Parent — same indent/alignment as the other menu items, plus a chevron */}
        <button
          onClick={() => setOpen((o) => !o)}
          className={`flex items-center transition-all duration-200 font-medium text-[15px] w-full gap-4 px-4 py-3 rounded-full ${isGroupActive
            ? 'bg-blue-600 text-white shadow-[0_4px_12px_-2px_rgba(37,99,235,0.5)]'
            : 'text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-[#31373f] cursor-pointer'}`}
        >
          <GIcon className={`w-[22px] h-[22px] flex-shrink-0 ${isGroupActive ? 'text-white' : 'text-gray-400 dark:text-gray-500'}`} strokeWidth={1.8} />
          <span className="flex-1 min-w-0 text-left truncate" title={label}>{label}</span>
          {!open && groupBadge > 0 && badgePill(groupBadge)}
          <ChevronDown className={`w-4 h-4 flex-shrink-0 transition-transform ${open ? 'rotate-180' : ''}`} />
        </button>

        {/* Children — a vertical line with a horizontal branch (├─) into each item.
            Geometry (don't eyeball these): the parent pill starts at 8px (ul's
            px-2) and its icon is centred at 8 + 16 (px-4) + 11 (half of 22px)
            = 35px, with its label at 8 + 16 + 22 + 16 (gap-4) = 62px.
            ml-[27px] puts the tree's vertical line exactly under the parent
            icon's centre; pl-[14px] then lands each child's icon flush with the
            parent's label. No mr — the child pills end level with the parent
            pill's right edge. */}
        {open && (
          <div className="mt-2.5 ml-[27px] pl-[14px] border-l border-gray-200 dark:border-gray-700 flex flex-col gap-1.5">
            {children.map((child) => {
              const CIcon = child.icon;
              const childActive = isUnder(location.pathname, child.href);
              return (
                <button
                  key={child.href}
                  onClick={() => { navigate(child.href); setIsOpen(false); }}
                  className={`relative w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-[14px] transition-colors duration-200
                    before:content-[''] before:absolute before:top-1/2 before:-translate-y-1/2 before:-left-[14px] before:w-[14px] before:h-px before:bg-gray-200 dark:before:bg-gray-700 ${childActive
                    ? 'bg-blue-50 dark:bg-[#31373f] text-blue-600 dark:text-blue-400 font-semibold'
                    : 'text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-[#31373f] hover:text-gray-700 dark:hover:text-gray-200 font-medium cursor-pointer'}`}
                >
                  <CIcon className={`w-[18px] h-[18px] flex-shrink-0 ${childActive ? 'text-blue-600 dark:text-blue-400' : 'text-gray-400 dark:text-gray-500'}`} strokeWidth={1.8} />
                  {/* min-w-0 + truncate: without them a long label (e.g.
                      "Recruitment & Onboarding") overflows the pill and spills
                      past its rounded background instead of ellipsising inside
                      it. `title` keeps the full text reachable on hover. */}
                  <span className="flex-1 min-w-0 text-left truncate" title={child.label}>{child.label}</span>
                  {child.badge > 0 && badgePill(child.badge, 'shrink-0')}
                </button>
              );
            })}
          </div>
        )}
      </li>
    );
  };

  return (
    <>
      {/* Mobile backdrop */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-20 md:hidden"
          onClick={() => setIsOpen(false)}
        />
      )}

      {/* Sidebar Container */}
      <div
        className={`fixed left-0 top-0 h-full bg-white dark:bg-[#262b33] border-r border-gray-200 dark:border-gray-800 text-gray-700 dark:text-gray-200 transition-all duration-300 z-[60] flex flex-col ${isCollapsed ? 'w-20' : 'w-64'
          } ${isOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0'}`}
      >

        {/* Logo Section with Collapse Arrow */}
        <div className={`pt-7 pb-6 flex items-center relative ${isCollapsed ? 'justify-center px-2' : 'pl-6 pr-5 gap-3'}`}>
          <Logo
            size="sm"
            showText={!isCollapsed}
            className="gap-3"
            imgClassName={isCollapsed ? '!w-[48px] !h-[48px]' : ''}
            textClassName="overflow-hidden"
          />

          {/* Collapse / expand toggle — always visible on the sidebar edge */}
          <button
            onClick={() => setIsCollapsed(!isCollapsed)}
            title={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            className="hidden md:flex absolute -right-3.5 top-1/2 -translate-y-1/2 w-7 h-7 bg-white dark:bg-[#31373f] border border-gray-200 dark:border-gray-700 rounded-full items-center justify-center text-gray-500 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 hover:border-blue-300 dark:hover:border-blue-500 shadow-[0_2px_8px_rgba(15,23,42,0.12)] z-50 transition"
          >
            {isCollapsed ? <ArrowRight className="w-4 h-4" /> : <ArrowLeft className="w-4 h-4" />}
          </button>
        </div>

        <div className="border-t border-gray-100 dark:border-gray-800"></div>

        {/* Menu Navigation */}
        <nav className="flex-1 overflow-y-auto py-4 overflow-x-hidden flex flex-col">
          <ul className="space-y-1 flex-1 px-2">

            {/* Dashboard — always first, for whoever actually has it. Managers
                always do; anybody else needs the Dashboard module ticked (see
                canSeeDashboard). Without it there is no button, and they were
                landed on their first module at login instead. */}
            {canSeeDashboard && (
            <li>
              <button
                onClick={() => { navigate(getDashboardRoute()); setIsOpen(false); }}
                title={isCollapsed ? "Dashboard" : ""}
                className={`flex items-center transition-all duration-200 font-medium text-[15px] ${isCollapsed
                  ? 'mx-auto justify-center w-[46px] h-[46px] rounded-xl'
                  : 'w-full gap-4 px-4 py-3 rounded-full'
                  } ${isRouteActive(getDashboardRoute())
                    ? `bg-blue-600 text-white shadow-[0_4px_12px_-2px_rgba(37,99,235,0.5)]`
                    : 'text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-[#31373f]'
                  }`}
              >
                <Home className={`w-[22px] h-[22px] flex-shrink-0 ${isRouteActive(getDashboardRoute()) ? 'text-white' : 'text-gray-400 dark:text-gray-500'}`} strokeWidth={1.8} />
                {!isCollapsed && <span className="flex-1 text-left whitespace-nowrap">Dashboard</span>}
              </button>
            </li>
            )}

            {/* Messages — private staff chat. Fixed (not menu-gated) and shown
                for every organisation user; see canMessage above. */}
            {canMessage && (
              <li>
                <button
                  onClick={() => { navigate('/manager/messages'); setIsOpen(false); }}
                  title={isCollapsed ? 'Messages' : ''}
                  className={`relative flex items-center transition-all duration-200 font-medium text-[15px] ${isCollapsed
                    ? 'mx-auto justify-center w-[46px] h-[46px] rounded-xl'
                    : 'w-full gap-4 px-4 py-3 rounded-full'
                    } ${isUnder(location.pathname, '/manager/messages')
                      ? `bg-blue-600 text-white shadow-[0_4px_12px_-2px_rgba(37,99,235,0.5)]`
                      : 'text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-[#31373f] cursor-pointer'
                    }`}
                >
                  <MessageSquare className={`w-[22px] h-[22px] flex-shrink-0 ${isUnder(location.pathname, '/manager/messages') ? 'text-white' : 'text-gray-400 dark:text-gray-500'}`} strokeWidth={1.8} />
                  {!isCollapsed && <span className="flex-1 text-left whitespace-nowrap">Messages</span>}
                  {unreadChats > 0 && (
                    // Expanded: a counter pill at the end of the row.
                    // Collapsed: the same count shrinks onto the icon corner,
                    // since there's no room for a pill on the rail.
                    <span
                      className={`bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center ${isCollapsed
                        ? 'absolute top-1 right-1 min-w-[16px] h-4 px-1'
                        : 'min-w-[20px] h-5 px-1.5'
                        }`}
                    >
                      {unreadChats > 99 ? '99+' : unreadChats}
                    </span>
                  )}
                </button>
              </li>
            )}

            {/* Audits & policies — the manager's by-role fallback.
                It is an assignable menu like Recruitment or Payroll, so when
                it HAS been granted the dynamic list below draws it. This entry
                covers the other case: a manager whose menu_ids predate the
                menu, who must never be locked out of their own audits. The two
                are mutually exclusive (see showAuditsFallback), so they cannot
                produce a duplicate row. */}
            {showAuditsFallback && (
              <li>
                <button
                  onClick={() => { navigate('/manager/audits-policies'); setIsOpen(false); }}
                  title={isCollapsed ? 'Audits & Policies' : ''}
                  className={`flex items-center transition-all duration-200 font-medium text-[15px] ${isCollapsed
                    ? 'mx-auto justify-center w-[46px] h-[46px] rounded-xl'
                    : 'w-full gap-4 px-4 py-3 rounded-full'
                    } ${isUnder(location.pathname, '/manager/audits-policies')
                      ? `bg-blue-600 text-white shadow-[0_4px_12px_-2px_rgba(37,99,235,0.5)]`
                      : 'text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-[#31373f] cursor-pointer'
                    }`}
                >
                  <ShieldCheck className={`w-[22px] h-[22px] flex-shrink-0 ${isUnder(location.pathname, '/manager/audits-policies') ? 'text-white' : 'text-gray-400 dark:text-gray-500'}`} strokeWidth={1.8} />
                  {!isCollapsed && <span className="flex-1 min-w-0 text-left truncate">Audits &amp; Policies</span>}
                </button>
              </li>
            )}

            {/* Agency workspace — five fixed links for an agency administrator.
                They hold no care-home menus, so nothing else is drawn. */}
            {isAgency && agencyNav.map((item) => {
              const Icon = item.icon;
              const active = isUnder(location.pathname, item.href);
              return (
                <li key={item.href}>
                  <button
                    onClick={() => { navigate(item.href); setIsOpen(false); }}
                    title={isCollapsed ? item.label : ''}
                    className={`relative flex items-center transition-all duration-200 font-medium text-[15px] ${isCollapsed
                      ? 'mx-auto justify-center w-[46px] h-[46px] rounded-xl'
                      : 'w-full gap-4 px-4 py-3 rounded-full'
                      } ${active
                        ? 'bg-blue-600 text-white shadow-[0_4px_12px_-2px_rgba(37,99,235,0.5)]'
                        : 'text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-[#31373f] cursor-pointer'
                      }`}
                  >
                    <Icon className={`w-[22px] h-[22px] flex-shrink-0 ${active ? 'text-white' : 'text-gray-400 dark:text-gray-500'}`} strokeWidth={1.8} />
                    {!isCollapsed && <span className="flex-1 min-w-0 text-left truncate">{item.label}</span>}
                    {item.badge > 0 && (
                      <span
                        className={`${item.amber ? 'bg-amber-500' : 'bg-red-500'} text-white text-[10px] font-bold rounded-full flex items-center justify-center ${isCollapsed
                          ? 'absolute top-1 right-1 min-w-[16px] h-4 px-1'
                          : 'min-w-[20px] h-5 px-1.5'
                          }`}
                      >
                        {item.badge > 99 ? '99+' : item.badge}
                      </span>
                    )}
                  </button>
                </li>
              );
            })}

            {/* Dynamic menu items sorted by priority */}
            {sortedMenuItems
              .filter((item) => item.code !== 'dashboard' && item.code !== 'admin_dashboard')
              .map((item) => {
                const Icon = item.icon;

                // Staff → expandable group (Staff Management + Staff Compliance).
                if (item.code === 'staffs') {
                  return renderMenuGroup({
                    item,
                    label: 'Staff',
                    open: staffOpen,
                    setOpen: setStaffOpen,
                    children: staffChildren,
                    collapsedHref: '/manager/staffs',
                    isGroupActive: isStaffRoute(location.pathname),
                  });
                }

                // Rota → expandable group (Schedule + Care Packages) — DOMICILIARY ONLY.
                if (item.code === 'duty_roster' && isDomiciliary) {
                  return renderMenuGroup({
                    item,
                    label: 'Rota',
                    open: rotaOpen,
                    setOpen: setRotaOpen,
                    children: rotaChildren,
                    collapsedHref: '/manager/domiciliary-schedule',
                    isGroupActive: isRotaRoute(location.pathname),
                  });
                }

                // Rota → expandable group (Schedule + Available Shifts) — CARE HOMES.
                if (item.code === 'duty_roster' && showCareRotaGroup) {
                  return renderMenuGroup({
                    item,
                    label: 'Rota',
                    open: careRotaOpen,
                    setOpen: setCareRotaOpen,
                    children: careRotaChildren,
                    collapsedHref: '/manager/duty-roster',
                    isGroupActive: isCareRotaRoute(location.pathname),
                  });
                }

                // Residents / Clients → expandable group once Enquiries &
                // Assessments is granted (the list + the enquiry pipeline).
                if (item.code === 'residents' && hasEnquiriesMenu) {
                  return renderMenuGroup({
                    item,
                    label: isDomiciliary ? 'Clients' : 'Residents',
                    open: residentsOpen,
                    setOpen: setResidentsOpen,
                    children: residentsChildren,
                    collapsedHref: '/manager/residences',
                    isGroupActive: isResidentsRoute(location.pathname),
                  });
                }

                // Care Records → expandable group (Care Notes + Handovers).
                if (item.code === 'care_notes') {
                  return renderMenuGroup({
                    item,
                    label: 'Care Records',
                    open: careRecordsOpen,
                    setOpen: setCareRecordsOpen,
                    children: careRecordChildren,
                    collapsedHref: '/manager/care-notes',
                    isGroupActive: isCareRecordRoute(location.pathname),
                  });
                }

                const active = isRouteActive(item.href);
                return (
                  <li key={item.id}>
                    <button
                      onClick={() => { navigate(item.href); setIsOpen(false); }}
                      title={isCollapsed ? item.label : ''}
                      className={`flex items-center transition-all duration-200 font-medium text-[15px] ${isCollapsed
                        ? 'mx-auto justify-center w-[46px] h-[46px] rounded-xl'
                        : 'w-full gap-4 px-4 py-3 rounded-full'
                        } ${active
                          ? `bg-blue-600 text-white shadow-[0_4px_12px_-2px_rgba(37,99,235,0.5)]`
                          : 'text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-[#31373f] cursor-pointer'
                        }`}
                    >
                      <Icon className={`w-[22px] h-[22px] flex-shrink-0 ${active ? 'text-white' : 'text-gray-400 dark:text-gray-500'}`} strokeWidth={1.8} />
                      {!isCollapsed && <span className="flex-1 min-w-0 text-left truncate" title={item.label}>{item.label}</span>}
                    </button>
                  </li>
                );
              })}
          </ul>

          {/* Divider + Help — always at the bottom of the nav */}
          <div className="mt-2 px-2">
            <div className="border-t border-gray-100 dark:border-gray-800 mb-2" />
            <button
              onClick={() => { navigate('/support'); setIsOpen(false); }}
              title={isCollapsed ? 'Support' : ''}
              className={`flex items-center transition-all duration-200 font-medium text-[15px] ${isCollapsed
                ? 'mx-auto justify-center w-[46px] h-[46px] rounded-xl'
                : 'w-full gap-4 px-4 py-3 rounded-full'
                } ${isRouteActive('/support')
                  ? `bg-blue-600 text-white shadow-[0_4px_12px_-2px_rgba(37,99,235,0.5)]`
                  : 'text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-[#31373f] cursor-pointer'
                }`}
            >
              <LifeBuoy className={`w-[22px] h-[22px] flex-shrink-0 ${isRouteActive('/support') ? 'text-white' : 'text-gray-400 dark:text-gray-500'}`} strokeWidth={1.8} />
              {!isCollapsed && <span className="flex-1 text-left whitespace-nowrap">Support</span>}
            </button>
          </div>
        </nav>

        {/* Logout moved to the Navbar profile dropdown — no duplicate here. */}

        {/* User profile footer removed — the avatar + name + email now live
            in the Navbar's profile dropdown, so duplicating them here would
            be visual noise. */}
      </div>

      {/* Collapsed-rail flyout submenu (Staff / Rota children). Rendered outside
          the sidebar's scroll container and positioned with `fixed` so it isn't
          clipped by the nav's overflow. */}
      {isCollapsed && flyout && (
        <div
          className="fixed z-[70] left-20 ml-1 w-56 bg-white dark:bg-[#262b33] border border-gray-100 dark:border-gray-800 rounded-2xl shadow-[0_16px_40px_rgba(15,23,42,0.16)] py-3 px-2"
          style={{ top: flyout.top }}
          onMouseEnter={() => openFlyout(flyout)}
          onMouseLeave={closeFlyoutSoon}
        >
          <p className="px-3 pb-2 text-[13px] font-bold text-gray-800 dark:text-gray-100">{flyout.label}</p>
          <div className="flex flex-col gap-1">
            {flyout.children.map((child) => {
              const CIcon = child.icon;
              const childActive = isUnder(location.pathname, child.href);
              return (
                <button
                  key={child.href}
                  onClick={() => { navigate(child.href); setIsOpen(false); setFlyout(null); }}
                  className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-[14px] transition ${childActive
                    ? 'bg-blue-600 text-white font-semibold'
                    : 'text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-[#31373f] font-medium cursor-pointer'}`}
                >
                  <CIcon className={`w-[18px] h-[18px] flex-shrink-0 ${childActive ? 'text-white' : 'text-gray-400 dark:text-gray-500'}`} strokeWidth={1.8} />
                  <span className="flex-1 min-w-0 text-left truncate" title={child.label}>{child.label}</span>
                  {child.badge > 0 && (
                    <span className="shrink-0 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center min-w-[20px] h-5 px-1.5">
                      {child.badge > 99 ? '99+' : child.badge}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </>
  );
};

export default Sidebar;